import type { Diagnostic } from '@vp/dskhema';
import { locale } from '../i18n';

/** DSkhema editor helpers: templates, cheat sheet and syntax highlighting (docs/ru/DSKHEMA.md §4). */

export const TEMPLATES: Record<'flow' | 'sequence' | 'blank', { ru: string; en: string }> = {
  flow: {
    ru: `схема вниз

старт: Начало (круг)
ввод: Ввести данные
проверка: Данные верны? (ромб)
сохранить: Сохранить в БД (цилиндр)
ошибка: Показать ошибку (скруглённый) [цвет=#fee2e2, контур=#b91c1c]

старт -> ввод -> проверка
проверка -> сохранить : да
проверка -> ошибка : нет
ошибка ..> ввод

группа Хранилище {
  сохранить
}`,
    en: `flow down

start: Start (circle)
input: Enter data
check: Is it valid? (diamond)
save: Save to DB (cylinder)
error: Show error (rounded) [fill=#fee2e2, stroke=#b91c1c]

start -> input -> check
check -> save : yes
check -> error : no
error ..> input

group Storage {
  save
}`,
  },
  sequence: {
    ru: `последовательность
нумерация

актёр польз: Пользователь
участник web: Веб-приложение
участник api: API

польз -> web : Нажимает «Оплатить»
web -> +api : POST /pay
api --> -web : 200 OK
web ->> польз : Показать чек
заметка над web, api : HTTPS

если Оплата прошла {
  web -> польз : Готово
} иначе {
  web -> польз : Ошибка
}`,
    en: `sequence
autonumber

actor user: User
participant web: Web app
participant api: API

user -> web : Clicks “Pay”
web -> +api : POST /pay
api --> -web : 200 OK
web ->> user : Show receipt
note over web, api : HTTPS

alt Payment succeeded {
  web -> user : Done
} else {
  web -> user : Error
}`,
  },
  blank: {
    ru: `схема вправо

а: Первый шаг
б: Второй шаг

а -> б`,
    en: `flow right

a: First step
b: Second step

a -> b`,
  },
};

export function template(kind: keyof typeof TEMPLATES): string {
  return TEMPLATES[kind][locale.value === 'ru' ? 'ru' : 'en'];
}

/** Cheat sheet rows: [syntax, meaning]. */
export function cheatsheet(): { title: string; rows: [string, string][] }[] {
  const ru = locale.value === 'ru';
  return ru
    ? [
        {
          title: 'Блок-схема',
          rows: [
            ['схема вниз | вправо | вверх | влево', 'заголовок и направление'],
            ['схема вправо плавно', 'плавные стрелки вместо угловых'],
            ['id: Подпись (форма) [стиль] @класс', 'узел'],
            ['прямоугольник скруглённый капсула круг эллипс ромб шестиугольник параллелограмм цилиндр документ треугольник ручной ввод данные скобка фигурная скобка экран', 'формы'],
            ['[мелкий пунктир]', 'мелкий пунктир контура'],
            ['а -> б   а -- б   а <-> б   а ..> б   а => б', 'стрелка, линия, двусторонняя, пунктир, толстая'],
            ['а -> б, в -> г : подпись', 'цепочки, несколько целей, подпись связи'],
            ['группа Название { ... }', 'группа узлов'],
            ['[цвет=#hex, контур=#hex, текст=#hex, толщина=2, пунктир]', 'стиль'],
            ['класс важное [цвет=#fecaca]   узел: Текст @важное', 'классы стилей'],
            ['// или %%', 'комментарий'],
          ],
        },
        {
          title: 'Последовательность',
          rows: [
            ['последовательность', 'заголовок'],
            ['участник id: Подпись   актёр id: Подпись', 'участники'],
            ['а -> б : текст   а --> б   а ->> б', 'запрос, ответ (пунктир), асинхронно'],
            ['а -> +б   б --> -а', 'включить / выключить активацию'],
            ['заметка над а, б : текст   заметка слева от а : текст', 'заметки'],
            ['цикл … { }   если … { } иначе … { }   опционально … { }   параллельно … { } и … { }', 'блоки'],
            ['нумерация', 'номера сообщений'],
          ],
        },
      ]
    : [
        {
          title: 'Flowchart',
          rows: [
            ['flow down | right | up | left', 'header and direction'],
            ['flow right curved', 'smooth arrows instead of angular'],
            ['id: Label (form) [style] @class', 'node'],
            ['rect rounded pill circle ellipse diamond hexagon parallelogram cylinder document triangle manual data bracket brace display', 'forms'],
            ['[fine-dashed]', 'fine stroke dash'],
            ['a -> b   a -- b   a <-> b   a ..> b   a => b', 'arrow, line, both ways, dotted, thick'],
            ['a -> b, c -> d : label', 'chains, several targets, link label'],
            ['group Name { ... }', 'group of nodes'],
            ['[fill=#hex, stroke=#hex, text=#hex, width=2, dashed]', 'style'],
            ['class important [fill=#fecaca]   node: Text @important', 'style classes'],
            ['// or %%', 'comment'],
          ],
        },
        {
          title: 'Sequence',
          rows: [
            ['sequence', 'header'],
            ['participant id: Label   actor id: Label', 'participants'],
            ['a -> b : text   a --> b   a ->> b', 'request, reply (dashed), async'],
            ['a -> +b   b --> -a', 'start / end activation'],
            ['note over a, b : text   note left of a : text', 'notes'],
            ['loop … { }   alt … { } else … { }   opt … { }   par … { } and … { }', 'blocks'],
            ['autonumber', 'message numbers'],
          ],
        },
      ];
}

const KEYWORDS = new Set(
  'плавно плавные угловые углом curved smooth orthogonal square схема блок-схема flow flowchart последовательность sequence группа group subgraph класс class участник participant актёр актер actor нумерация autonumber активировать activate деактивировать deactivate заметка note цикл loop если alt опционально opt параллельно par иначе else и and над over слева left справа right от of вниз вправо вверх влево down right up left td tb lr bt rl'.split(
    ' ',
  ),
);

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const TOKEN = /(\s(?:\/\/|%%).*$|^(?:\/\/|%%).*$)|("[^"]*"?)|(<->|-->|->>|\.\.>|\.\.|=>|->|--)|(\[[^\]]*\]?)|(\([^)]*\)?)|(@[\p{L}\p{N}_-]+)|(:)|([\p{L}\p{N}_.-]+)/gu;

/** One line of source as highlighted HTML. */
export function highlightLine(line: string): string {
  let out = '';
  let last = 0;
  let afterColon = false;
  for (const m of line.matchAll(TOKEN)) {
    const i = m.index ?? 0;
    out += esc(line.slice(last, i));
    last = i + m[0].length;
    const [tok, comment, str, op, style, form, cls, colon, word] = m;
    if (comment) out += `<span class="t-com">${esc(tok)}</span>`;
    else if (afterColon && !style && !form && !cls) out += `<span class="t-lbl">${esc(tok)}</span>`;
    else if (str) out += `<span class="t-str">${esc(tok)}</span>`;
    else if (op) out += `<span class="t-op">${esc(tok)}</span>`;
    else if (style) out += `<span class="t-sty">${esc(tok)}</span>`;
    else if (form) out += `<span class="t-form">${esc(tok)}</span>`;
    else if (cls) out += `<span class="t-cls">${esc(tok)}</span>`;
    else if (colon) {
      out += `<span class="t-op">:</span>`;
      afterColon = true;
    } else if (word && KEYWORDS.has(word.toLowerCase())) out += `<span class="t-kw">${esc(tok)}</span>`;
    else out += `<span class="t-id">${esc(tok)}</span>`;
  }
  return out + esc(line.slice(last));
}

/** Highlighted source with error / warning lines marked. */
export function highlight(source: string, diagnostics: Diagnostic[]): string {
  const sev = new Map<number, 'error' | 'warning'>();
  for (const d of diagnostics) if (sev.get(d.line) !== 'error') sev.set(d.line, d.severity);
  return source
    .split('\n')
    .map((l, i) => {
      const s = sev.get(i + 1);
      const html = highlightLine(l) || ' ';
      return s ? `<span class="l-${s}">${html}</span>` : html;
    })
    .join('\n');
}
